import type {
  AcceptSubscriptionPauseInput,
  CancelSubscriptionInput,
  CreateSubscriptionInput,
  DowngradeImpactPreviewDto,
  StripeAddressDto,
  StripeAddressInput,
  StripeCheckoutDto,
  StripeCheckoutInput,
  StripeInvoiceDto,
  StripePaymentMethodDto,
  StripePriceDto,
  StripePromoCodeDto,
  StripeSetupIntentDto,
  StripeSubscriptionDto,
  StripeSubscriptionPaymentResumeDto,
  StripeSubscriptionResponseDto,
  StripeSubscriptionUpgradeInput,
  StripeUpgradePreviewDto,
  SubscriptionExitFlowDto,
  WorkspaceUsageDto,
} from '@/features/billing/types';
import { http } from '@/http/axios';

export const billingService = {
  getSubscription: (workspaceId: number): Promise<StripeSubscriptionDto> =>
    http
      .get<StripeSubscriptionDto>(`/workspaces/${workspaceId}/billing/subscription`)
      .then((r) => r.data),
  getUsage: (workspaceId: number): Promise<WorkspaceUsageDto> =>
    http.get<WorkspaceUsageDto>(`/workspaces/${workspaceId}/billing/usage`).then((r) => r.data),
  getUpgradePreview: (workspaceId: number, newPriceId: string): Promise<StripeUpgradePreviewDto> =>
    http
      .get<StripeUpgradePreviewDto>(
        `/workspaces/${workspaceId}/billing/subscription/upgrade-preview`,
        { params: { newPriceId } },
      )
      .then((r) => r.data),
  getDowngradeImpactPreview: (
    workspaceId: number,
    newPriceId: string,
  ): Promise<DowngradeImpactPreviewDto> =>
    http
      .get<DowngradeImpactPreviewDto>(
        `/workspaces/${workspaceId}/billing/subscription/downgrade-impact-preview`,
        { params: { newPriceId } },
      )
      .then((r) => r.data),
  upgradeSubscription: (
    workspaceId: number,
    input: StripeSubscriptionUpgradeInput,
  ): Promise<StripeSubscriptionResponseDto> =>
    http
      .put<StripeSubscriptionResponseDto>(`/workspaces/${workspaceId}/billing/subscription`, input)
      .then((r) => r.data),
  createSubscription: (
    workspaceId: number,
    input: CreateSubscriptionInput,
  ): Promise<StripeSubscriptionResponseDto> =>
    http
      .post<StripeSubscriptionResponseDto>(`/workspaces/${workspaceId}/billing/subscription`, input)
      .then((r) => r.data),
  resumeSubscriptionPayment: (workspaceId: number): Promise<StripeSubscriptionPaymentResumeDto> =>
    http
      .get<StripeSubscriptionPaymentResumeDto>(
        `/workspaces/${workspaceId}/billing/subscription/resume-payment`,
      )
      .then((r) => r.data),
  cancelSubscription: (workspaceId: number, input: CancelSubscriptionInput): Promise<void> =>
    http
      .post<void>(`/workspaces/${workspaceId}/billing/subscription/cancel`, input)
      .then(() => undefined),
  resumeSubscription: (workspaceId: number): Promise<StripeSubscriptionDto> =>
    http
      .post<StripeSubscriptionDto>(`/workspaces/${workspaceId}/billing/subscription/resume`)
      .then((r) => r.data),
  resumeSubscriptionPause: (workspaceId: number): Promise<StripeSubscriptionDto> =>
    http
      .post<StripeSubscriptionDto>(`/workspaces/${workspaceId}/billing/subscription/pause/resume`)
      .then((r) => r.data),
  startSubscriptionExitFlow: (workspaceId: number): Promise<SubscriptionExitFlowDto> =>
    http
      .post<SubscriptionExitFlowDto>(`/workspaces/${workspaceId}/billing/subscription/exit-flow`)
      .then((r) => r.data),
  acceptRetentionDowngrade: (workspaceId: number): Promise<SubscriptionExitFlowDto> =>
    http
      .post<SubscriptionExitFlowDto>(
        `/workspaces/${workspaceId}/billing/subscription/exit-flow/downgrade/accept`,
      )
      .then((r) => r.data),
  declineRetentionDowngrade: (workspaceId: number): Promise<SubscriptionExitFlowDto> =>
    http
      .post<SubscriptionExitFlowDto>(
        `/workspaces/${workspaceId}/billing/subscription/exit-flow/downgrade/decline`,
      )
      .then((r) => r.data),
  acceptRetentionPause: (
    workspaceId: number,
    input: AcceptSubscriptionPauseInput,
  ): Promise<SubscriptionExitFlowDto> =>
    http
      .post<SubscriptionExitFlowDto>(
        `/workspaces/${workspaceId}/billing/subscription/exit-flow/pause/accept`,
        input,
      )
      .then((r) => r.data),
  declineRetentionPause: (workspaceId: number): Promise<SubscriptionExitFlowDto> =>
    http
      .post<SubscriptionExitFlowDto>(
        `/workspaces/${workspaceId}/billing/subscription/exit-flow/pause/decline`,
      )
      .then((r) => r.data),
  confirmRetentionCancellation: (
    workspaceId: number,
    input: CancelSubscriptionInput,
  ): Promise<SubscriptionExitFlowDto> =>
    http
      .post<SubscriptionExitFlowDto>(
        `/workspaces/${workspaceId}/billing/subscription/exit-flow/cancel/confirm`,
        input,
      )
      .then((r) => r.data),
  getPrices: (workspaceId: number, promoCode?: string): Promise<StripePriceDto[]> =>
    http
      .get<StripePriceDto[]>(`/workspaces/${workspaceId}/billing/prices`, {
        params: promoCode ? { promoCode } : undefined,
      })
      .then((r) => r.data),
  getPromoCode: (workspaceId: number, code: string): Promise<StripePromoCodeDto> =>
    http
      .get<StripePromoCodeDto>(
        `/workspaces/${workspaceId}/billing/promo-codes/${encodeURIComponent(code)}`,
      )
      .then((r) => r.data),
  getPaymentMethods: (workspaceId: number): Promise<StripePaymentMethodDto[]> =>
    http
      .get<StripePaymentMethodDto[]>(`/workspaces/${workspaceId}/billing/payment-methods`)
      .then((r) => r.data),
  getInvoices: (workspaceId: number): Promise<StripeInvoiceDto[]> =>
    http.get<StripeInvoiceDto[]>(`/workspaces/${workspaceId}/billing/invoices`).then((r) => r.data),
  downloadInvoicesZip: (workspaceId: number, invoiceIds: string[]): Promise<Blob> =>
    http
      .post<Blob>(`/workspaces/${workspaceId}/billing/invoices/download-zip`, invoiceIds, {
        responseType: 'blob',
      })
      .then((r) => r.data),
  setPaymentMethodDefault: (workspaceId: number, paymentMethodId: string): Promise<void> =>
    http
      .put<void>(`/workspaces/${workspaceId}/billing/payment-methods/${paymentMethodId}/default`)
      .then(() => undefined),
  detachPaymentMethod: (workspaceId: number, paymentMethodId: string): Promise<void> =>
    http
      .delete<void>(`/workspaces/${workspaceId}/billing/payment-methods/${paymentMethodId}`)
      .then(() => undefined),
  getAddress: (workspaceId: number): Promise<StripeAddressDto> =>
    http.get<StripeAddressDto>(`/workspaces/${workspaceId}/billing/address`).then((r) => r.data),
  updateAddress: (workspaceId: number, input: StripeAddressInput): Promise<void> =>
    http.put<void>(`/workspaces/${workspaceId}/billing/address`, input).then(() => undefined),
  createSetupIntent: (workspaceId: number): Promise<StripeSetupIntentDto> =>
    http
      .post<StripeSetupIntentDto>(`/workspaces/${workspaceId}/billing/setup-intent`)
      .then((r) => r.data),
  createCheckout: (workspaceId: number, input: StripeCheckoutInput): Promise<StripeCheckoutDto> =>
    http
      .post<StripeCheckoutDto>(`/workspaces/${workspaceId}/billing/checkout`, input)
      .then((r) => r.data),
};
